import { readProjectFile } from '../../engine/host.js';
import { api } from '../../lib/api.js';
import {
  bytesToBase64,
  composeSheet,
  sliceSheet,
  usedColors,
  type PixelDoc,
} from '../../lib/pixel-doc.js';

/** Decodes a project image into RGBA pixels. */
export async function readImagePixels(projectId: number, path: string): Promise<{ pixels: Uint8ClampedArray; width: number; height: number }> {
  const bytes = await readProjectFile(projectId, path);
  const bitmap = await createImageBitmap(new Blob([bytes as BlobPart]));
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const g = canvas.getContext('2d')!;
  g.drawImage(bitmap, 0, 0);
  const data = g.getImageData(0, 0, bitmap.width, bitmap.height);
  return { pixels: data.data, width: bitmap.width, height: bitmap.height };
}

/** Builds a document from an image sliced into frames. */
export function docFromImage(
  base: PixelDoc,
  img: { pixels: Uint8ClampedArray; width: number; height: number },
  frameW: number,
  frameH: number,
): PixelDoc {
  const frames = sliceSheet(img.pixels, img.width, img.height, frameW, frameH);
  const doc: PixelDoc = { ...base, width: frameW, height: frameH, cells: frames.map((f) => [f]), animations: {} };
  doc.palette = usedColors(doc);
  return doc;
}

/** Writes `<name>.png` (the sheet) and `<name>.sprite.json` next to the document. */
export async function exportPng(projectId: number, docPath: string, doc: PixelDoc): Promise<string> {
  const { pixels, width, height } = composeSheet(doc);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG encoding failed'))), 'image/png'),
  );
  const base = docPath.replace(/\.pixel\.json$/i, '');
  const pngPath = `${base}.png`;
  const name = pngPath.slice(pngPath.lastIndexOf('/') + 1);
  await api.put(`/projects/${projectId}/file`, {
    path: pngPath,
    content: bytesToBase64(new Uint8Array(await blob.arrayBuffer())),
    encoding: 'base64',
  });
  const sheet: Record<string, unknown> = {
    format: 'prism.sprite/1',
    image: name,
    frameSize: [doc.width, doc.height],
    animations: doc.animations,
  };
  if (doc.pivot) sheet.pivot = doc.pivot;
  await api.put(`/projects/${projectId}/file`, { path: `${base}.sprite.json`, content: `${JSON.stringify(sheet, null, 2)}\n` });
  return pngPath;
}
